import { useSuspenseQuery } from "@apollo/client";
import {
  Button,
  type ChartConfig,
  ChartContainer,
  DataTable,
  DataTableColumnHeader,
  PrimaryFullScreenSpinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import {
  formatCompactCurrencyAmount,
  formatCurrencyAmount,
  NotFoundError,
} from "@repo/utils";
import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import Decimal from "decimal.js";
import { DateTime } from "luxon";
import { Suspense } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CurrencyRateUI } from "~/components/Amount/CurrencyRateUI";
import { DetailRow, DetailSubRow } from "~/components/DetailRow/DetailRow";
import { graphql } from "~/gql/client";
import type { Currency } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute(
  "/__auth/products_/$productId/unit-prices",
)({
  head: () => opsHead("Product Unit Prices"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const UtcDateTimeUI = ({ at }: { at: Date }) => {
  const utcDateTime = DateTime.fromJSDate(at).setZone("UTC");

  return (
    <div className="flex-1 space-x-1 text-sm">
      <div className="inline-flex items-baseline font-semibold text-sm">
        {utcDateTime.toFormat("yyyy-MM-dd")}
      </div>
      <div className="inline-flex items-baseline text-sm">
        {utcDateTime.toFormat("HH:mm")}
      </div>
      <div className="inline-flex items-baseline text-xs">
        {utcDateTime.toFormat("ZZZZ")}
      </div>
    </div>
  );
};

type UnitPriceHistoryItem = {
  at: Date;
  price: Decimal;
};

type UnitPriceHistoryChartDatum = {
  at: Date;
  dateLabel: string;
  price: number;
  priceLabel: string;
};

const CHART_HISTORY_MONTHS = 12;

const getColumns = (currency: Currency): ColumnDef<UnitPriceHistoryItem>[] => [
  {
    id: "at",
    accessorFn: (row) => row.at.getTime(),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="As of" />
    ),
    cell: ({ row }) => <UtcDateTimeUI at={row.original.at} />,
  },
  {
    id: "price",
    accessorFn: (row) => row.price.toNumber(),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Value" />
    ),
    cell: ({ row }) => (
      <CurrencyRateUI currency={currency} amount={row.original.price} />
    ),
  },
];

function View() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminProductUnitPrices($productId: ID!) {
        product(id: $productId) {
          id
          ...on FundProduct {
            unitPriceHistory {
              id
              currency
              unitPrices {
                targetUnit
                at
                price
              }
            }
          }
        }
      }
    `),
    {
      variables: {
        productId: params.productId ?? "",
      },
    },
  );

  if (!data.product || data.product.__typename !== "FundProduct") {
    throw new NotFoundError();
  }

  const unitPriceHistory = data.product.unitPriceHistory;
  if (!unitPriceHistory) {
    throw new Error("expected unit price history to be non-null");
  }

  const unitPriceHistoryItems: UnitPriceHistoryItem[] = (
    unitPriceHistory.unitPrices ?? []
  )
    .map((item) => ({
      at: item.at,
      price: new Decimal(item.price),
    }))
    .sort((a, b) => b.at.getTime() - a.at.getTime());
  const latestUnitPrice = unitPriceHistoryItems[0];

  if (!latestUnitPrice) {
    return <div className="mt-4">No unit price history for this product.</div>;
  }

  const chartWindowStart = DateTime.fromJSDate(latestUnitPrice.at)
    .minus({ months: CHART_HISTORY_MONTHS })
    .toMillis();
  const chartData = unitPriceHistoryItems
    .filter((item) => item.at.getTime() >= chartWindowStart)
    .sort((a, b) => a.at.getTime() - b.at.getTime())
    .map((item) => ({
      at: item.at,
      dateLabel: DateTime.fromJSDate(item.at)
        .setZone("UTC")
        .toFormat("d MMM yy"),
      price: item.price.toNumber(),
      priceLabel: formatCurrencyAmount({
        currency: unitPriceHistory.currency,
        amount: item.price,
        notation: "standard",
      }),
    }));

  const chartConfig = {
    price: {
      label: "Unit price",
      color: "hsl(var(--color-info-600))",
    },
  } satisfies ChartConfig;

  return (
    <div>
      <DetailRow label="Latest price">
        <DetailSubRow
          label="Value"
          value={
            <CurrencyRateUI
              currency={unitPriceHistory.currency}
              amount={latestUnitPrice.price}
            />
          }
        />
        <DetailSubRow
          label="As Of"
          value={<UtcDateTimeUI at={latestUnitPrice.at} />}
        />
      </DetailRow>

      <DetailRow label="History">
        <Tabs defaultValue="chart">
          <TabsList size="xs" className="self-start">
            <TabsTrigger value="chart">Chart</TabsTrigger>
            <TabsTrigger value="table">Table</TabsTrigger>
          </TabsList>
          <TabsContent value="chart" className="mt-4">
            <div className="rounded-lg border p-4">
              <div className="mb-4 text-secondary text-sm">
                Showing the last {CHART_HISTORY_MONTHS} months of unit price
                history.
              </div>
              <ChartContainer
                config={chartConfig}
                className="h-[280px] w-full min-w-0"
              >
                <LineChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--color-neutral-200))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="dateLabel"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={24}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    width={96}
                    tickFormatter={(value) =>
                      formatCompactCurrencyAmount({
                        currency: unitPriceHistory.currency,
                        amount: new Decimal(value),
                        hideCurrencyCode: true,
                      })
                    }
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      const point = payload?.[0]?.payload as
                        | UnitPriceHistoryChartDatum
                        | undefined;

                      if (!active || !point) {
                        return null;
                      }

                      const pointDateTime = DateTime.fromJSDate(
                        point.at,
                      ).setZone("UTC");

                      return (
                        <div className="rounded-lg border bg-primary p-3 shadow-popover">
                          <div className="text-secondary text-xs">
                            {pointDateTime.toFormat("yyyy-MM-dd HH:mm ZZZZ")}
                          </div>
                          <div className="font-medium text-primary text-sm">
                            {point.priceLabel}
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke={chartConfig.price.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ChartContainer>
            </div>
          </TabsContent>
          <TabsContent value="table" className="mt-4">
            <DataTable
              data={unitPriceHistoryItems}
              columns={getColumns(unitPriceHistory.currency)}
              initialState={{
                pagination: {
                  pageSize: 20,
                },
              }}
              columnSortProps={{
                id: "at",
                desc: true,
              }}
              className="overflow-hidden rounded-lg border"
            />
          </TabsContent>
        </Tabs>
      </DetailRow>

      <div>
        <Button
          type="button"
          variant="ghost"
          onClick={() =>
            navigate({
              to: `/products/${params.productId}/unit-prices/update`,
            })
          }
        >
          Update unit price data
        </Button>
      </div>
    </div>
  );
}
